import { useState, useEffect } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import SideMenu from './SideMenu';
import SearchBar from '../Search/SearchBar';
import { useNotifications } from '../../context/NotificationsContext';
import { useRestaurantEta, useDefaultRestaurant } from '../../hooks/useRestaurantEta';
import { useColorHeader } from '../../hooks/useColorHeader';

export default function HeaderUnified({
  onBusinessSelect,
  onOpenLocationModal,
  onSearchTap,
  userAddressProp,
  whiteMode = false,
  // Sticky colapsable: con `stickyOffset` (px de la fila de ubicación + safe
  // area), el header se pega con `top: -stickyOffset`. Así la dirección se
  // desliza fuera de vista y el MISMO buscador queda anclado arriba, en vez de
  // aparecer una copia flotante desde el borde superior.
  stickyOffset = null,
  headerRef,
  searchRowRef,
  // 0..1 — cuánto blanco lleva el fondo del buscador. Vira de la imagen del
  // header a blanco para empatar con las barras de categorías/filtros que se le
  // pegan debajo. Va atado al scroll, así que a media transición se ve
  // translúcido en vez de cambiar de golpe.
  searchWhite = 0,
  // Px desde el pie del header hasta la mitad del buscador. El fondo del
  // header llega solo hasta ahí: la mitad de abajo del buscador queda sobre la
  // sección de abajo, que sube ese mismo tanto por detrás. 0 = fondo completo.
  mitadBuscador = 0,
  // Ya anclado arriba, el fondo vuelve a cubrir el buscador completo para que
  // no se vea el contenido pasando por debajo de su mitad inferior.
  anclado = false,
}) {
  const { unreadCount } = useNotifications();
  const eta = useRestaurantEta(); // minutos de manejo usuario → restaurante
  const restaurant = useDefaultRestaurant();
  const headerBg = restaurant?.headerBackground; // fondo del header (URL, dashboard)
  // Sin imagen: el color de arriba del fondo de la sección promocional.
  const colorLiso = useColorHeader(restaurant);
  // Color del texto del header (decisión manual del admin según el fondo).
  const darkText = restaurant?.headerTextColor === 'dark';
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [userAddress, setUserAddress] = useState('');
  const [addressDetail, setAddressDetail] = useState(null);

  // User address from prop or localStorage
  useEffect(() => {
    if (userAddressProp) {
      setUserAddress(userAddressProp);
    } else {
      const stored = localStorage.getItem('userAddress');
      if (stored) setUserAddress(stored);
    }
    const storedDetail = localStorage.getItem('userAddressDetail');
    if (storedDetail) {
      try { setAddressDetail(JSON.parse(storedDetail)); } catch {}
    }
  }, [userAddressProp]);

  // Listen for location updates to refresh address detail
  useEffect(() => {
    const handleLocationUpdate = () => {
      const stored = localStorage.getItem('userAddressDetail');
      if (stored) {
        try { setAddressDetail(JSON.parse(stored)); } catch {}
      }
      const addr = localStorage.getItem('userAddress');
      if (addr) setUserAddress(addr);
    };
    window.addEventListener('locationUpdated', handleLocationUpdate);
    return () => window.removeEventListener('locationUpdated', handleLocationUpdate);
  }, []);

  return (
    <>
      <header
        ref={headerRef}
        className={`${stickyOffset != null ? 'sticky' : 'relative'} z-40 pt-[env(safe-area-inset-top)] ${
          whiteMode ? 'bg-white' : ''
        }`}
        style={stickyOffset != null ? { top: `-${stickyOffset}px` } : undefined}
      >
        {/* Fondo del header en su propia capa: termina a la mitad del buscador
            (o cubre todo una vez anclado). Va detrás de las filas, que son
            relative. */}
        {!whiteMode && (
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 bg-cover bg-center bg-header-bg pointer-events-none"
            style={{
              bottom: anclado ? 0 : mitadBuscador,
              ...(headerBg ? { backgroundImage: `url(${headerBg})` } : {}),
              ...(colorLiso ? { backgroundColor: colorLiso } : {}),
            }}
          />
        )}

        {/* Row 1 — Ubicación + Campana (solo en modo normal) */}
        {!whiteMode && (
          <div className="relative px-4 pt-3 pb-1.5">
            <div className="flex items-center justify-between mb-0">
              {/* Location */}
              <button
                onClick={onOpenLocationModal}
                className="flex items-center gap-3 min-w-0 flex-1"
              >
                {/* Recuadro de tiempo: ETA de manejo del usuario al restaurante */}
                <div className="flex flex-col items-center justify-center flex-shrink-0 w-12 h-13 rounded-lg bg-forest-900 text-white shadow-sm">
                  {eta != null ? (
                    <>
                      {/* El pr centra ópticamente el número. Dos cosas lo corrían a la
                          derecha: el letter-spacing negativo se aplica también después
                          del último dígito (encoge el ancho medido), y la tinta de los
                          dígitos itálicos de Futura sobresale de su avance. Medido en
                          vivo: con 0.33em el desfase de 1→120 queda en ±1.5px. */}
                      <span className="text-[21px] font-bold italic tracking-[-0.11em] pr-[0.33em] font-display leading-none">{eta}</span>
                      <span className="text-[11px] font-extrabold italic tracking-wider leading-none">MINS</span>
                    </>
                  ) : (
                    <span className="text-xl font-extrabold leading-none">··</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <span className={`font-bold text-[16px] leading-tight truncate max-w-[210px] ${darkText ? 'text-gray-900' : 'text-white'}`}>
                      {addressDetail?.city || userAddress || 'Tu ubicación'}
                    </span>
                    <CaretDown size={15} weight="bold" className={`flex-shrink-0 ${darkText ? 'text-gray-700' : 'text-white/80'}`} />
                  </div>
                  <p className={`text-[13px] leading-tight truncate max-w-[200px] text-left ${darkText ? 'text-gray-600' : 'text-white/60'}`}>
                    {userAddress || 'Selecciona tu dirección'}
                  </p>
                </div>
              </button>

              {/* Notifications */}
              <div className="flex items-center flex-shrink-0 pr-0">
                <button
                  onClick={() => setIsSideMenuOpen(true)}
                  className="relative mr-1 p-2 -m-2"
                  aria-label="Abrir menú"
                >
                  <svg width="24" height="22" viewBox="0 0 22 20" fill="none" aria-hidden="true">
                    <rect x="0" y="1" width="21" height="3" rx="1.5" fill={darkText ? '#111827' : 'white'} />
                    <rect x="0" y="8.5" width="16" height="3" rx="1.5" fill={darkText ? '#111827' : 'white'} />
                    <rect x="0" y="16" width="21" height="3" rx="1.5" fill={darkText ? '#111827' : 'white'} />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-header-badge-bg text-white text-[10px] font-bold rounded-full border border-white/30 flex items-center justify-center px-0.5">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Row 2 — Buscador. Es la parte del header que queda visible cuando se
            colapsa en sticky. */}
        <div ref={searchRowRef} className="relative">
          {/* Capa blanca cuya opacidad sigue al scroll: al principio se ve la
              imagen del header, a media transición queda translúcida y al final
              es blanco pleno, empatando con categorías y filtros. Sin transición
              CSS a propósito — la anima el scroll, no un temporizador. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-white pointer-events-none"
            style={{ opacity: searchWhite }}
          />
          <div className="relative">
            {/* El input cambia a su variante gris pasada la mitad del viraje: en
                blanco pleno, un input blanco se perdería contra el fondo. */}
            <SearchBar whiteMode={whiteMode || searchWhite > 0.5} onTap={onSearchTap} />
          </div>
        </div>
      </header>

      {/* Side Menu */}
      <SideMenu
        isOpen={isSideMenuOpen}
        onClose={() => setIsSideMenuOpen(false)}
        onBusinessSelect={onBusinessSelect}
      />
    </>
  );
}
